import { FC } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { Box, Collapse, List, Stack } from '@mui/material';

import { ContentType } from '@models/Content';

import KeyValuePair from './KeyValuePair';
import NewKeyValuePair from './NewKeyValuePair';
import { KeyValue } from './types';
import usePredefinedParams from './usePredefinedParams';
import { getReservedParamKeysForType } from './utils';

type CustomParamsProps = {
  contentType?: ContentType;
  handleRemovePair: (key: string) => void;
  handleSetPair: (keyValuePair: KeyValue) => void;
  params?: Record<string, string>;
};

const CustomParams: FC<CustomParamsProps> = ({
  contentType,
  handleRemovePair,
  handleSetPair,
  params,
}) => {
  const reservedKeys = getReservedParamKeysForType(contentType);

  const { predefinedParams } = usePredefinedParams();

  const predefinedKeys = predefinedParams.map(({ value }) => value);

  const sortedKeyValuePairs = params
    ? Object.entries(params)
        .reduce(
          (acc: KeyValue[], [key, value]) =>
            !reservedKeys?.includes(key) ? [...acc, { key, value }] : acc,
          []
        )
        .sort((a, b) => {
          const aIsPredefined = predefinedKeys.includes(a.key);
          const bIsPredefined = predefinedKeys.includes(b.key);

          return aIsPredefined && !bIsPredefined
            ? -1
            : !aIsPredefined && bIsPredefined
              ? 1
              : a.key.localeCompare(b.key);
        })
    : [];

  const hasPredefinedParams = sortedKeyValuePairs.some(({ key }) =>
    predefinedParams.some(({ value }) => key === value)
  );

  return (
    <Stack width={1}>
      {sortedKeyValuePairs.length > 0 && (
        <List dense>
          <TransitionGroup>
            {sortedKeyValuePairs.map(({ key, value }) => (
              <Collapse key={key}>
                <KeyValuePair
                  handleRemovePair={handleRemovePair}
                  handleSetPair={handleSetPair}
                  inset={hasPredefinedParams}
                  keyValuePair={{ key, value: value as string }}
                />
              </Collapse>
            ))}
          </TransitionGroup>
        </List>
      )}

      <Box p={2}>
        <NewKeyValuePair
          handleSetPair={handleSetPair}
          reservedKeys={reservedKeys}
          usedKeys={sortedKeyValuePairs.map(({ key }) => key)}
        />
      </Box>
    </Stack>
  );
};

export default CustomParams;
